import React, { useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { DateTime } from 'luxon';
import classnames from 'classnames';
import { Iframe } from 'modules/common/components';
import { useMeContext } from 'modules/me';
import { GenericCircularProgess } from '../../App/menu/items/Settings/GenericCircularProgess';
import { useSmartTimesheetContext } from '../smartDiaryNotes/SmartTimesheetContextProvider';
import TimesheetNotFound from './ErrorPage/TimesheetNotFound';
import { useTimesheetFrame } from './useTimesheetFrame';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    width: '100%',
    height: '99%',
    flexDirection: 'column'
  },
  heading: {
    padding: theme.spacing(0, 1, 1.5, 3.75)
  },
  frameNoDisplay: {
    width: 0,
    height: 0
  },
  frame: {
    border: 0,
    width: '100%',
    height: '99%'
  },
  withPointer: {
    pointerEvents: 'auto'
  },
  withoutPointer: {
    pointerEvents: 'none'
  },
  loadingContainer: {
    display: 'flex',
    width: '100%',
    height: '99%',
    justifyContent: 'center',
    alignItems: 'center'
  }
}));

export const EmbeddedTimesheet: React.FC = () => {
  const classes = useStyles();
  const [embedTSKey, setEmbedTSKey] = useState<string>(uuidv4());
  const [isLoading, setIsLoading] = React.useState(true);
  const onFrameLoaded = React.useCallback(() => {
    setIsLoading(false);
  }, [setIsLoading]);
  const { tsFrameId, tsUrl } = useTimesheetFrame();
  const { currentPeriod } = useSmartTimesheetContext();

  const timesheetForDate = useMemo(() => {
    setIsLoading(true);
    setEmbedTSKey(uuidv4());
    return currentPeriod?.startDate || DateTime.local().toISODate();
  }, [currentPeriod]);

  const me = useMeContext();
  const hasAccess = me?.permissions?.hasTimesheetAccess;

  return !hasAccess ? (
    <TimesheetNotFound />
  ) : (
    <div className={classes.root}>
      <Typography className={classes.heading} variant="h4">
        <FormattedMessage id="embeddedTimesheet.title" />
      </Typography>
      {isLoading ? <GenericCircularProgess /> : null}

      {
        <Iframe
          key={embedTSKey}
          frameId={tsFrameId}
          title="timesheet"
          className={classnames({
            [classes.frameNoDisplay]: isLoading,
            [classes.frame]: !isLoading,
            [classes.withPointer]: !isLoading
          })}
          onFrameLoad={onFrameLoaded}
          src={`${tsUrl}embedded/my/timesheet/${timesheetForDate}?noNavigation=true&app=zt`}
        />
      }
    </div>
  );
};
